import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { CategoryCard, Label } from "@/components/CategoryCard";
import { CATEGORY_ICONS, CATEGORY_TINTS } from "@/lib/categories";
import { useServiceI18n } from "@/lib/serviceI18n";
import { ArrowLeft, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";

const Diensten = () => {
  const { t } = useTranslation();
  const { catName, catDesc, subName, subDiensten } = useServiceI18n();
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/service-categories").then((r) => setCategories(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#F5A623] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const selected = categoryId ? categories.find((c) => c.id === categoryId) : null;

  if (selected) {
    const Icon = CATEGORY_ICONS[selected.icon] || Sparkles;
    const tint = CATEGORY_TINTS[selected.color] || CATEGORY_TINTS.slate;
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <section className="bg-gradient-to-b from-amber-50/40 via-white to-[#F8FAFC]">
          <div className="max-w-5xl mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-12">
            <Link to="/diensten" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#1A2B4A]" data-testid="back-to-services">
              <ArrowLeft size={14} /> {t("services.all_categories")}
            </Link>
            <div className="mt-6 flex items-start gap-5 flex-wrap">
              <div className={`w-16 h-16 rounded-2xl ${tint.bg} ${tint.text} ${tint.border} border flex items-center justify-center`}>
                <Icon size={32} />
              </div>
              <div className="flex-1 min-w-[260px]">
                <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#1A2B4A] tracking-tight">{catName(selected)}</h1>
                <p className="text-slate-600 mt-2">{catDesc(selected)}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#F8FAFC] pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="grid md:grid-cols-2 gap-4">
              {selected.subcategories.map((s) => (
                <div key={s.id} className="beecenter-card p-5 flex flex-col" data-testid={`subcategory-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading font-bold text-[#1A2B4A] text-base">{subName(s)}</h3>
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {s.labels.map((l) => <Label key={l} label={l} />)}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{subDiensten(s)}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to={`/onboarding?diensten=${encodeURIComponent(selected.subcategories.map((s) => s.id).join(","))}`} className="btn-amber" data-testid="cta-start-onboarding">
                {t("services.find_match")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Overview of all 9 categories
  const totalServices = categories.reduce((acc, c) => acc + c.subcategories.length, 0);
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="bg-gradient-to-b from-amber-50/40 via-white to-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-14 md:pt-20 pb-10">
          <span className="pill-amber">{t("services.badge")}</span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1A2B4A] mt-4 leading-[1.05] tracking-tight">
            {t("services.title")}
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-3xl">{t("services.subtitle")}</p>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-700">
            <span className="flex items-center gap-2"><Sparkles size={16} className="text-[#F5A623]" /> {totalServices} {t("services.providers_count")}</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#22C55E]" /> {t("services.verified_pill")}</span>
          </div>
        </div>
      </section>
      <section className="bg-[#F8FAFC] pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-2">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((c) => <CategoryCard key={c.id} category={c} />)}
          </div>
          <div className="mt-12 text-center">
            <Link to="/onboarding" className="btn-amber" data-testid="overview-cta">
              {t("services.find_match")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Diensten;
